import { useState, useRef, useEffect } from 'react';
import { ErrorMessage, useFormikContext } from 'formik';
import { cn } from '@/utils/cn';
import { Icon } from '@iconify/react';

interface FileUploadProps {
  name: string;
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  minFiles?: number;
  className?: string;
  required?: boolean;
}

const FileUpload = ({
  name,
  label,
  accept = 'image/*',
  multiple = false,
  maxFiles = 5,
  minFiles,
  className,
  required,
}: FileUploadProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setFieldValue, setFieldError, values } = useFormikContext<any>();

  const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);

    // Filter out files that exceed the max size
    const acceptedFiles = fileArray.filter((f) => f.size <= MAX_FILE_SIZE);
    const rejectedFiles = fileArray.filter((f) => f.size > MAX_FILE_SIZE);

    if (rejectedFiles.length > 0) {
      const names = rejectedFiles.map((f) => f.name).join(', ');
      setFieldError(name, `File lebih dari 2 MB: ${names}`);
    } else {
      // Clear previous error
      setFieldError(name, '');
    }

    // Respect maxFiles and multiple flag
    const limitedFiles = multiple
      ? acceptedFiles.slice(0, maxFiles)
      : acceptedFiles.length
        ? [acceptedFiles[0]]
        : [];

    // Create preview URLs for accepted files
    const newPreviews = limitedFiles.map((file) => URL.createObjectURL(file));

    // Clean up old preview URLs
    previews.forEach((url) => URL.revokeObjectURL(url));
    setPreviews(newPreviews);

    // Set form value
    if (limitedFiles.length === 0) {
      setFieldValue(name, multiple ? [] : null);
    } else {
      setFieldValue(name, multiple ? limitedFiles : limitedFiles[0]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileChange(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeFile = (index: number) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);

    if (multiple) {
      const currentFiles = values[name] as File[];
      const newFiles = currentFiles.filter((_, i) => i !== index);
      setFieldValue(name, newFiles);
    } else {
      setFieldValue(name, null);
    }

    URL.revokeObjectURL(previews[index]);
  };

  // Sync previews when form value changes (e.g., resetForm should clear previews)
  useEffect(() => {
    const current = values[name];
    if (!current || (Array.isArray(current) && current.length === 0)) {
      // Revoke existing previews and clear
      previews.forEach((url) => URL.revokeObjectURL(url));
      setPreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    // If values[name] contains File objects but previews are empty, regenerate previews
    else if (current && previews.length === 0) {
      const fileArray: File[] = Array.isArray(current) ? current : [current];
      const newPreviews = fileArray.map((file) => URL.createObjectURL(file));
      setPreviews(newPreviews);
    }
  }, [values[name]]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm text-gray-600 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={cn(
          'border-2 border-dashed rounded-md p-4 text-center transition-colors',
          dragOver
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50',
          className
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFileChange(e.target.files)}
          className="hidden"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
        >
          <Icon icon="mdi:upload" />
          Choose Files
        </button>

        <p className="text-sm text-gray-500 mt-2">
          {multiple ? (
            <>
              *Upload {minFiles ? `min. ${minFiles} dan ` : ''}maks. {maxFiles} gambar,
              masing-masing{' '}
            </>
          ) : (
            '*Upload 1 gambar dengan '
          )}
          maksimal filesize adalah 2 MB
        </p>
      </div>

      {/* Preview Images */}
      {previews.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-md border"
              />
              <button
                type="button"
                onClick={() => removeFile(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
    </div>
  );
};

export default FileUpload;
